import { useCallback, useMemo } from "react";
import { usePharmaFormFields } from "../../context/pharmaFormFieldsContext";
import { CheckboxField } from "../../data-model/pharmaPaFormModel";
import { checkIfErrorExists } from "../../pages/pharmaPaForm/utils/checkIfErrorExists";
import { getNestedValue } from "../../utils/getNestedValue";

interface CheckboxFieldProps {
  field: CheckboxField;
  fieldDetails?: Record<string, any>;
  data?: any;
  originalData?: any;
  shouldEmitLogEvent?: boolean;
}

const RenderCheckboxField = (props: CheckboxFieldProps) => {
  const { formFieldsData, setFormFieldsData } = usePharmaFormFields();

  const options = props.field.options ?? [];
  const initialDataValue = getNestedValue(props?.data, props.field.key) ?? [];

  const getSelectedValues = useCallback((): string[] => {
    if (props?.field?.readOnly) {
      return formFieldsData?.[props?.field?.key]?.filledValue ?? [];
    }
    if (formFieldsData?.[props?.field?.key]?.isFieldDirty) {
      return formFieldsData?.[props?.field?.key]?.filledValue ?? [];
    }
    if (
      initialDataValue &&
      Array.isArray(initialDataValue) &&
      initialDataValue.length > 0
    ) {
      return initialDataValue;
    }
    return props?.field?.default ?? [];
  }, [formFieldsData, props.field, initialDataValue]);

  const selectedValues = useMemo(
    () => getSelectedValues(),
    [getSelectedValues],
  );

  const handleCheckboxChange = (optionValue: string, checked: boolean) => {
    const newValues = checked
      ? [...selectedValues, optionValue]
      : selectedValues.filter((v) => v !== optionValue);

    const originalDataValue =
      getNestedValue(props?.originalData, props.field.key) ?? [];

    setFormFieldsData((prev) => ({
      ...prev,
      [props?.field?.key]: {
        ...prev[props?.field?.key],
        filledValue: newValues,
        isFieldDirty:
          JSON.stringify(originalDataValue) !== JSON.stringify(newValues),
      },
    }));
  };

  return (
    <div
      key={props?.field?.key}
      className="checkbox__container flex-1"
      style={{
        maxWidth: props?.field?.width ? `${props.field.width}%` : "100%",
      }}
    >
      <div className="checkbox__label mb-1 flex gap-1 text-h12 font-semibold">
        {props?.field?.label}
        {props?.field?.isRequired && (
          <div className="checkbox__label--required text-tertiaryRed-5">*</div>
        )}
      </div>
      <div className="flex flex-col gap-1">
        {options.map((option) => (
          <label
            key={option.value}
            className="flex cursor-pointer items-center gap-2"
          >
            <input
              type="checkbox"
              checked={selectedValues.includes(option.value)}
              onChange={(e) =>
                handleCheckboxChange(option.value, e.target.checked)
              }
              disabled={props?.field?.readOnly}
              className="h-4 w-4 rounded border-gray-300"
            />
            <span className="text-sm">{option.label}</span>
          </label>
        ))}
      </div>
      <div className="checkbox__error text-tiny font-normal text-[#cd0202]">
        {checkIfErrorExists(
          formFieldsData,
          props.field.key,
          props?.field?.readOnly,
        )
          ? `${props.field.validationMessage ?? "This field is required"}`
          : ""}
      </div>
      {(props?.field?.additionalInfoHeader ||
        props?.field?.additionalInfoContent) && (
        <div className="section-additonal-info flex w-full flex-col gap-2 bg-secondaryYellow-11 p-3">
          <div className="section-additonal-info__header text-tiny font-bold">
            {props?.field?.additionalInfoHeader ?? ""}
          </div>
          <div className="section-additonal-info__content text-[0.8125rem] font-normal">
            {props?.field?.additionalInfoContent ?? ""}
          </div>
        </div>
      )}
    </div>
  );
};

export default RenderCheckboxField;
